import { Link, useNavigate } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { motion } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";
import { useRole } from "@/contexts/RoleContext";
import LogoutButton from "@/components/LogoutButton";
import { ShieldCheck, FileCheck, Globe2, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import HomeScreenBanner from "@/components/HomeScreenBanner";

export default function Landing() {
  const { t } = useLang();
  const navigate = useNavigate();
  const { actingAsAdmin } = useRole();
  const home = actingAsAdmin ? "/admin" : "/dashboard";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="brutal-border border-l-0 border-r-0 border-t-0 bg-background sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4">
          <Link to="/home" className="inline-flex" data-testid="brand-link">
            <Logo />
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link to="/" className="font-mono-label text-xs text-muted-foreground hidden sm:inline" data-testid="change-lang-link">
              <Globe2 size={14} className="inline mr-1" /> Language
            </Link>
            <Show when="signed-out">
              <button
                data-testid="header-sign-in"
                onClick={() => navigate("/sign-in")}
                className="min-h-[44px] px-4 py-2 text-sm font-mono-label border border-[#262626] hover:border-white transition-colors"
              >
                {t("signIn")}
              </button>
              <button
                data-testid="header-get-started"
                onClick={() => navigate("/sign-up")}
                className="hidden sm:inline-flex items-center min-h-[44px] cta-primary px-4 py-2 text-sm font-mono-label"
              >
                {t("getStarted")}
              </button>
            </Show>
            <Show when="signed-in">
              <Link to={home} className="hidden sm:inline-flex items-center min-h-[44px] px-4 py-2 text-sm font-mono-label border border-[#262626] hover:border-white transition-colors" data-testid="header-dashboard">
                {actingAsAdmin ? "Customers" : t("dashboard")}
              </Link>
              <div className="hidden sm:block"><UserButton /></div>
              <span className="sm:hidden"><LogoutButton testid="header-logout-button-mobile" compact /></span>
              <span className="hidden sm:inline-flex"><LogoutButton testid="header-logout-button" /></span>
            </Show>
          </nav>
        </div>
      </header>

      <HomeScreenBanner />
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="font-mono-label text-xs text-[#FFD600] mb-4" data-testid="hero-tag">{t("heroTag")}</div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6" data-testid="hero-title">
            {t("heroTitle")}
          </h1>
          <p className="text-lg text-muted-foreground max-w-xl mb-8">{t("heroSub")}</p>
          <div className="flex flex-wrap gap-3">
            <Show when="signed-out">
              <button data-testid="hero-cta-start" onClick={() => navigate("/sign-up")} className="cta-primary px-6 py-3 font-mono-label text-sm inline-flex items-center gap-2">
                {t("getStarted")} <ArrowRight size={16} />
              </button>
              <button data-testid="hero-cta-signin" onClick={() => navigate("/sign-in")} className="px-6 py-3 border border-[#262626] hover:border-white font-mono-label text-sm transition-colors">
                {t("signIn")}
              </button>
            </Show>
            <Show when="signed-in">
              <button data-testid="hero-cta-wizard" onClick={() => navigate(actingAsAdmin ? "/admin" : "/wizard")} className="cta-primary px-6 py-3 font-mono-label text-sm inline-flex items-center gap-2">
                {actingAsAdmin ? "Go to Customers" : "Continue setup"} <ArrowRight size={16} />
              </button>
              {!actingAsAdmin && (
                <Link to="/dashboard" className="px-6 py-3 border border-[#262626] hover:border-white font-mono-label text-sm transition-colors">
                  {t("dashboard")}
                </Link>
              )}
            </Show>
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="relative">
          <div className="brutal-border aspect-[4/5] overflow-hidden">
            <img
              src="https://images.unsplash.com/photo-1631744591853-998c4308bbb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxzZW1pJTIwdHJ1Y2slMjBvbiUyMGhpZ2h3YXl8ZW58MHx8fHwxNzkyODA5NDIzfDA&ixlib=rb-4.1.0&q=85"
              alt="Semi truck on highway"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute bottom-3 left-3 sm:-bottom-6 sm:-left-6 bg-[#FFD600] text-[#0A0A0A] p-4 sm:p-5 brutal-border">
            <div className="font-mono-label text-[10px] mb-1">USDOT · MC · BMC-91</div>
            <div className="font-heading font-black text-2xl leading-none">All in one flow.</div>
          </div>
        </motion.div>
      </section>

      {/* Grid features */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="brutal-border grid md:grid-cols-3">
          {[
            { icon: FileCheck, title: "FMCSA Filings", body: "USDOT, MC Authority, UCR, IRP, IFTA — tracked in one dashboard." },
            { icon: ShieldCheck, title: "Insurance Ready", body: "BMC-84 bond and BMC-91 insurance filing guidance from day one." },
            { icon: Globe2, title: "7 Languages", body: "English, Spanish, French, Punjabi, Russian, Chinese, Hindi — your choice." },
          ].map((f, i) => (
            <div key={i} className="p-8 border-r border-b border-[#262626] bg-[#0A0A0A]">
              <f.icon size={22} className="text-[#0055FF] mb-4" />
              <div className="font-heading font-black text-xl mb-2">{f.title}</div>
              <div className="text-sm text-muted-foreground">{f.body}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[#262626] py-8 px-6 text-center">
        <div className="font-mono-label text-[10px] text-muted-foreground">© {new Date().getFullYear()} CARRIERLAUNCH · OPERATING AUTHORITY MADE SIMPLE · <Link to="/privacy" className="underline hover:text-white" data-testid="footer-privacy-link">PRIVACY POLICY</Link></div>
      </footer>
    </div>
  );
}
